import * as React from "react";
import Layout from "@components/layout";
import Seo from "@components/seo";
import Hero from "@components/Hero";
import DataSection from "@components/DataSection";
import ClientsSection from "@components/ClientsSection";
import ValuePropositionSection from "@components/ValuePropositionSection";
import FeatureSection from "@components/FeatureSection";
import LandingTestimonialCTA from "@components/LandingTestimonialCTA";
import { Slice, graphql } from "gatsby";

import { DeepNonNullable } from "ts-essentials";
import { LocaleSlugMap, updateSlugsToLocale } from "@utils/i18n";

export const query = graphql`
  query FrontPageData($locale: String!) {
    strapiFrontpage(locale: { eq: $locale }) {
      locale
      pageTitle
      hero {
        title {
          data {
            title
          }
        }
        subtitle
        emailInputPlaceholder
        emailInputButtonText
        buttonSubtitle
        image {
          id
          alternativeText
          localFile {
            publicURL
            extension
            childImageSharp {
              gatsbyImageData(width: 560, placeholder: BLURRED)
            }
          }
        }
      }
      numbers {
        id
        title
        number
        dataType
      }
      valuePropositionTitle {
        data {
          valuePropositionTitle
        }
      }
      valuePropositions {
        id
        title
        description
        linkTitle
        linkUrl
      }
      features {
        id
        title
        subtitle {
          data {
            childMarkdownRemark {
              ...MarkdownNode
            }
          }
        }
        image {
          id
          alternativeText
          localFile {
            childImageSharp {
              gatsbyImageData(placeholder: BLURRED, quality: 80)
            }
            extension
            publicURL
          }
        }
        featurePoints {
          id
          title
          subtitle {
            data {
              childMarkdownRemark {
                ...MarkdownNode
              }
            }
          }
        }
        productPage {
          slug
        }
      }
      customer_case {
        companyLogo {
          alternativeText
          localFile {
            childImageSharp {
              gatsbyImageData(height: 50, placeholder: BLURRED, layout: FIXED)
            }
          }
        }
        title
        slug
        excerpt1 {
          data {
            childMarkdownRemark {
              ...MarkdownNode
            }
          }
        }
        excerpt2 {
          data {
            childMarkdownRemark {
              ...MarkdownNode
            }
          }
        }
        companyPersonName
        companyPersonTitle
        buttonText
      }
      book_demo {
        id
      }
      seo {
        metaDescription
        metaKeywords
      }
    }
    strapiGenericText(locale: { eq: $locale }) {
      errorText
    }
    strapiCase(locale: { eq: $locale }) {
      slug
    }
    strapiClientLogo(locale: { eq: $locale }) {
      title
    }
  }
`;

type Props = {
  data: DeepNonNullable<Queries.FrontPageDataQuery>;
  pageContext: {
    locale: string;
    localeSlugMap: LocaleSlugMap;
  };
};

const IndexPage = ({ data, pageContext }: Props) => {
  updateSlugsToLocale(data, pageContext.locale);
  const {
    pageTitle,
    hero,
    numbers,
    valuePropositions,
    valuePropositionTitle,
    features,
    customer_case,
    book_demo: { id: bookDemoId },
    seo,
  } = data.strapiFrontpage;

  const { slug: casesSlug } = data.strapiCase;

  const landingTestimonialProps = {
    ...customer_case,
    slug: `../${casesSlug}/${customer_case.slug}`,
  };

  const { title, subtitle, emailInputButtonText, buttonSubtitle } = hero;

  return (
    <Layout
      locale={pageContext.locale}
      localeSlugMap={pageContext.localeSlugMap}
    >
      {/**
       Overflow-x:hidden is needed
       because features are overflowing on the right
       in the mobile design, but we don't want a scrollbar
       Also, other pages need the overflow (like blog)
       for white background
       **/}
      <div className="overflow-x-hidden">
        <Seo
          title={pageTitle}
          metaDescription={seo.metaDescription}
          metaKeywords={seo.metaKeywords}
          {...pageContext}
        />
        <Hero
          {...{
            ...hero,
            title: title?.data?.title || "",
            subtitle,
            emailInputButtonText,
            buttonSubtitle,
          }}
        />
        <DataSection data={numbers} />
        <ClientsSection title={data.strapiClientLogo.title} />
        <ValuePropositionSection
          data={valuePropositions}
          title={valuePropositionTitle?.data?.valuePropositionTitle || ""}
        />
        <FeatureSection data={features} />
        <LandingTestimonialCTA
          {...landingTestimonialProps}
          locale={pageContext.locale}
        />
        <div className="mx-auto mt-16 flex flex-row justify-center">
          <Slice alias="bookDemo" authorId={bookDemoId} allowEmpty />
        </div>
      </div>
    </Layout>
  );
};

export default IndexPage;
